import React, { Fragment, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faLocationDot,
  faArrowPointer,
  faCartShopping,
} from "@fortawesome/sharp-light-svg-icons"
import NexmartCartContext from "../../context/NexmartCartContext"

import Button from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Button"
import useNexmartRetailers from "../../hooks/useNexmartRetailers"
import { Dialog, Transition } from "@headlessui/react"
import BuyProductPanel from "./BuyProductPanel"
import { faXmarkLarge } from "@fortawesome/sharp-light-svg-icons"

const ProductShoppingPanel = ({ cartProduct, variantSku }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [buyModalStartTab, setBuyModalStartTab] = useState("dealer")
  const { addItemToCart } = useContext(NexmartCartContext)
  const { t } = useTranslation()
  const [hasRetailers, retailers, nexmartNotConfigured] =
    useNexmartRetailers(variantSku)

  const onLocalDealerClickCallback = () => {
    setIsModalOpen(true)
    setBuyModalStartTab("dealer")
    window.adf && adf.ClickTrack({}, 2861695, "Button Kaufen", {})
  }
  const onOnlineRetailerClickCallback = () => {
    setIsModalOpen(true)
    setBuyModalStartTab("online")
    window.adf && adf.ClickTrack({}, 2861695, "Button Kaufen", {})
  }

  const closeModalHandler = () => {
    setIsModalOpen(false)
  }

  return (
    <div>
      {process.env.GATSBY_NEXMART_DEALER_LOCATOR_FEATURE === "true" && (
        <Button
          className="flex items-center w-full mt-6 text-left md:mt-8"
          btnType="primary"
          onClick={() => onLocalDealerClickCallback()}
          id="FindLocalDealer"
        >
          {t("generic.products.shopping_panel.buy_at_dealer")}
          <FontAwesomeIcon
            icon={faLocationDot}
            size="1x"
            className="inline-block ml-auto"
          />
        </Button>
      )}
      {/* Nexmart Cart Integration */}
      {process.env.GATSBY_NEXMART_CART_FEATURE === "true" && (
        <Button
          className="flex items-center w-full mt-6 text-left md:mt-4"
          btnType="outlineRed"
          onClick={() => addItemToCart(cartProduct)}
          id="AddToCart"
        >
          {t("generic.products.shopping_panel.add_to_cart")}
          <FontAwesomeIcon
            icon={faCartShopping}
            size="1x"
            className="inline-block ml-auto"
          />
        </Button>
      )}
      {/* END Nexmart Cart Integration */}
      {(nexmartNotConfigured !== true ||
        process.env.GATSBY_NEXMART_DEALER_LOCATOR_FEATURE === "true") && (
        <div className="p-4 mt-6 border md:mt-8 border-ro-gray-100">
          {nexmartNotConfigured !== true && (
            <div className="flex items-start gap-2 pb-4 mb-4 border-b border-ro-gray-75">
              <div className="flex items-center justify-center flex-shrink-0 w-6 h-6">
                <FontAwesomeIcon
                  icon={faArrowPointer}
                  size="1x"
                  className="inline-block"
                />
              </div>
              <div>
                <h5 className="font-bold">
                  {t("generic.products.shopping_panel.buy_online")}
                </h5>
                <p className="mt-0.5">
                  {hasRetailers !== undefined && (
                    <>
                      {t(
                        "generic.products.shopping_panel.buy_online_text_first"
                      )}{" "}
                      {retailers.length === 0 ? (
                        <>
                          {t(
                            "generic.products.shopping_panel.buy_online_text_shops_zero"
                          )}
                        </>
                      ) : (
                        <button
                          className="inline underline transition-colors duration-300 underline-offset-[5px] text-ro-red hover:text-ro-red-800 active:text-ro-gray-900"
                          onClick={() => onOnlineRetailerClickCallback()}
                          id="FindOnlineRetailer"
                        >
                          {t(
                            "generic.products.shopping_panel.buy_online_text_shops",
                            {
                              count: retailers.length,
                            }
                          )}
                        </button>
                      )}{" "}
                      {t(
                        "generic.products.shopping_panel.buy_online_text_last"
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
          {process.env.GATSBY_NEXMART_DEALER_LOCATOR_FEATURE === "true" && (
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 shrink-0">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  size="1x"
                  className="inline-block w-5"
                />
              </div>
              <div>
                <h5 className="font-bold">
                  {t("generic.products.shopping_panel.find_dealer")}
                </h5>
                <p className="mt-0.5">
                  {t("generic.products.shopping_panel.find_dealer_text_first")}{" "}
                  <button
                    className="inline underline transition-colors duration-300 underline-offset-[5px] text-ro-red hover:text-ro-red-800 active:text-ro-gray-900"
                    onClick={() => onLocalDealerClickCallback()}
                    id="FindLocalDealerLink"
                  >
                    {t("generic.products.shopping_panel.find_dealer_text_last")}
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => closeModalHandler()}
        >
          <Transition.Child
            as={Fragment}
            onClick={() => closeModalHandler()}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 block transition-opacity bg-ro-gray-900-50o"></div>
          </Transition.Child>
          <div className="fixed inset-0 z-10">
            <div className="min-h-full pt-12 pb-0 text-center md:py-8 md:flex md:items-stretch md:justify-center sm:items-center md:px-8">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-105"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-105"
              >
                <Dialog.Panel
                  as="div"
                  className="relative flex w-full text-base text-left transition transform max-w-[1184px] h-[calc(100dvh-3rem)] md:h-fit max-h-[calc(100dvh-3rem)] lg:max-h-[calc(100dvh-4rem)] overflow-hidden"
                >
                  <div className="relative w-full bg-ro-white">
                    <BuyProductPanel
                      sku={variantSku}
                      suppressNexmart={nexmartNotConfigured}
                      startAtTab={buyModalStartTab}
                    />
                    <button
                      className="absolute top-0 right-0 z-20 w-12 h-12 transition-colors duration-300 bg-white hover:bg-ro-gray-75 active:bg-ro-gray-200"
                      onClick={() => closeModalHandler()}
                    >
                      <FontAwesomeIcon icon={faXmarkLarge} size="1x" />
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  )
}

export default ProductShoppingPanel
